import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Model, { helper, Providers } from '@tripian/model';
import { api, providers } from '@tripian/core';

import ICombinedState from '../redux/model/ICombinedState';
import { saveError, changeLoadingReservations, changeReservations } from '../redux/action/trip';

const useReservation = () => {
  const [loadingReservation, setLoadingReservation] = useState<boolean>(false);
  const { cityId, reservations, loadingReservations } = useSelector((state: ICombinedState) => ({
    cityId: state.trip.reference?.city.id,
    reservations: state.trip.reservations,
    loadingReservations: state.trip.loading.reservations,
  }));
  const dispatch = useDispatch();

  const yelpReservations = useMemo(() => reservations?.filter((rs) => rs.provider === Model.PROVIDER_NAME.YELP), [reservations]);

  const reservationsFetch = useCallback((): Promise<Model.UserReservation[]> => {
    if (cityId === undefined) {
      // eslint-disable-next-line no-console
      console.warn('reservationsFetch called with undefined cityId');
      return Promise.resolve([]);
    }
    dispatch(changeLoadingReservations(true));

    return api
      .reservations(cityId, undefined, undefined, undefined, moment().subtract(1, 'days').format('YYYY-MM-DD'))
      .then(
        (userReservations: Model.UserReservation[]) =>
          // dispatch(changeReservations(userReservations));
          userReservations,
      )

      .catch((reservationsFetchError) => {
        dispatch(saveError('reservationsFetch', 'Fetch Reservations', reservationsFetchError));
        throw reservationsFetchError;
      });
    // .finally(() => {
    //   dispatch(changeLoadingReservations(false));
    // });
  }, [cityId, dispatch]);

  const reservationAdd = useCallback(
    (reservationRequest: Model.UserReservationRequest, showLoading = true): Promise<Model.UserReservation[]> => {
      setLoadingReservation(showLoading);

      return api.combo
        .reservationAdd(reservationRequest, cityId || 0)
        .then((newUserReservations: Model.UserReservation[]) => {
          dispatch(changeReservations(newUserReservations));
          return newUserReservations;
        })

        .catch((reservationAddError) => {
          dispatch(saveError('reservationAdd', 'Add Reservation', reservationAddError));
          throw reservationAddError;
        })
        .finally(() => {
          if (showLoading) setLoadingReservation(false);
        });
    },
    [cityId, dispatch],
  );

  const reservationUpdate = useCallback(
    (reservation: Model.UserReservation, save = true): Promise<Model.UserReservation[]> => {
      if (save) setLoadingReservation(true);

      return api.combo
        .reservationUpdate(reservation, cityId || 0)
        .then((newUserReservations: Model.UserReservation[]) => {
          if (save) dispatch(changeReservations(newUserReservations));
          return newUserReservations;
        })

        .catch((reservationUpdateError) => {
          dispatch(saveError('reservationUpdate', 'Update Reservation', reservationUpdateError));
          throw reservationUpdateError;
        })
        .finally(() => {
          if (save) setLoadingReservation(false);
        });
    },
    [cityId, dispatch],
  );

  const reservationDelete = useCallback(
    (reservationId: number, save = true): Promise<Model.UserReservation[]> => {
      if (save) setLoadingReservation(true);

      return api.combo
        .reservationDelete(reservationId, cityId || 0)
        .then((newReservations: Model.UserReservation[]) => {
          if (save) dispatch(changeReservations(newReservations));
          return newReservations;
        })

        .catch((reservationDeleteError) => {
          dispatch(saveError('reservationDelete', 'Delete Reservation', reservationDeleteError));
          throw reservationDeleteError;
        })
        .finally(() => {
          if (save) setLoadingReservation(false);
        });
    },
    [cityId, dispatch],
  );

  // yelp reservation cancel with tripian api reservation delete request. Cancel in our forms.
  const reservationCancel = useCallback(
    (reservationId: string) => {
      // yelp request loading
      setLoadingReservation(true);

      // tripian yelp reservation data
      const currentReservation = yelpReservations?.find((reservation) => (reservation.value as Providers.Yelp.ReservationInfo).reservation_id === reservationId);

      if (currentReservation) {
        return providers.yelp
          ?.reservationCancel(reservationId)
          .then(() => reservationDelete(currentReservation.id))
          .catch((yelpReservationCancelError) => {
            dispatch(saveError('yelpReservationCancel', 'Yelp Reservation Cancel', yelpReservationCancelError));
            throw yelpReservationCancelError;
          })
          .finally(() => {
            setLoadingReservation(false);
          });
      }

      return undefined;
    },

    [dispatch, reservationDelete, yelpReservations],
  );

  // Manuel edit or cancel in mail or Yelp.com check
  const yelpReservationStatusCheck = useCallback(
    async (apiReservations: Model.UserReservation[]) => {
      const apiYelpReservations = apiReservations.filter((rs) => rs.provider === Model.PROVIDER_NAME.YELP);
      const reservationStatusPromises: Promise<Providers.Yelp.ReservationStatusResponse | undefined>[] = [];
      const allYelpReservations: Model.UserReservation[] = [];

      apiYelpReservations.forEach((tripianYelpReservation, index) => {
        const reservationPromises = async (userReservation: Model.UserReservation): Promise<Providers.Yelp.ReservationStatusResponse | undefined> => {
          await helper.delay(index * 1000);
          return providers.yelp?.reservationStatus((userReservation.value as Providers.Yelp.ReservationInfo).reservation_id);
        };

        const reservationStatusResponse = reservationPromises(tripianYelpReservation);
        allYelpReservations.push(tripianYelpReservation);
        reservationStatusPromises.push(reservationStatusResponse);
      });

      try {
        const values = await Promise.all(reservationStatusPromises);
        values.forEach((value: Providers.Yelp.ReservationStatusResponse | undefined, pIndex) => {
          const currentReservation: Model.UserReservation = allYelpReservations[pIndex];

          const currentReservationInfo: Providers.Yelp.ReservationInfo = currentReservation.value as Providers.Yelp.ReservationInfo;

          if (value && !value.active) {
            reservationDelete(currentReservation?.id, false);
          } else if (
            (value && value.covers !== currentReservationInfo.reservation_details.covers) ||
            (value && currentReservationInfo.reservation_details.date !== value.date) ||
            (value && currentReservationInfo.reservation_details.time !== value?.time.slice(0, 5))
          ) {
            const differentReservation = { ...currentReservation };
            (differentReservation.value as Providers.Yelp.ReservationInfo).reservation_details = {
              ...(differentReservation.value as Providers.Yelp.ReservationInfo).reservation_details,
              covers: value.covers,
              date: value.date,
              time: value.time.slice(0, 5),
            };

            reservationUpdate(differentReservation, false);
          }
        });
      } catch (error) {
        dispatch(saveError('reservationStatus', 'Reservation status error', error ? (error as string) : ''));
      }
    },
    [dispatch, reservationDelete, reservationUpdate],
  );

  useEffect(() => {
    if (reservations === undefined && loadingReservations === false && cityId !== undefined) {
      reservationsFetch().then((apiReservationData: Model.UserReservation[]) => {
        const apiYelpReservationData: Model.UserReservation[] = apiReservationData?.filter((rs) => rs.provider === Model.PROVIDER_NAME.YELP);

        if (apiYelpReservationData.length > 0) {
          yelpReservationStatusCheck(apiReservationData).then(() => {
            reservationsFetch().then((statusCheckedReservationData: Model.UserReservation[]) => {
              dispatch(changeReservations(statusCheckedReservationData));
              dispatch(changeLoadingReservations(false));
            });
          });
        } else {
          dispatch(changeReservations(apiReservationData));
          dispatch(changeLoadingReservations(false));
        }
      });
    }
  }, [loadingReservations, reservations, reservationsFetch, cityId, yelpReservationStatusCheck, dispatch]);

  return {
    reservations,
    loadingReservations,
    loadingReservation,
    yelpReservations,
    reservationsFetch,
    reservationAdd,
    reservationUpdate,
    reservationDelete,
    reservationCancel,
  };
};

export default useReservation;
